import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, ShoppingBag } from "lucide-react";
import { VRIJ_WERK, VRIJ_WERK_INTRO, PRINT_SIZES, waLink, mailtoLink, BRAND } from "@/data/site";
import CTASection from "@/components/CTASection";

export const VW_TEST_IDS = {
  card: "vw-card",
  modal: "vw-modal",
  modalClose: "vw-modal-close",
  sizeOption: "vw-size-option",
  orderBtn: "vw-order-btn",
};

const VrijWerk = () => {
  const [openId, setOpenId] = useState(null);
  const [sizeId, setSizeId] = useState(PRINT_SIZES[1].id);

  const active = VRIJ_WERK.find((w) => w.id === openId) || null;
  const activeSize = PRINT_SIZES.find((s) => s.id === sizeId) || PRINT_SIZES[0];

  const openWork = (id) => {
    setOpenId(id);
    setSizeId(PRINT_SIZES[1].id);
  };

  const orderMsg =
    active &&
    `Hallo Eric, ik wil graag "${active.title}" (${active.year}) bestellen — formaat: ${activeSize.label} (${activeSize.price}).`;

  return (
    <>
      {/* Hero */}
      <section className="relative pt-40 md:pt-48 pb-16 bg-white">
        <div className="container-x text-center max-w-3xl mx-auto">
          <p className="eyebrow mb-4">Vrij werk</p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05]">
            Kunst die je <span className="italic text-gold">kunt kopen.</span>
          </h1>
          <p className="mt-8 text-neutral-600 leading-relaxed max-w-2xl mx-auto">{VRIJ_WERK_INTRO}</p>
          <p className="mt-6 font-script text-gold text-3xl">— Eric Lemmens</p>
        </div>
      </section>

      {/* Gallery */}
      <section className="bg-beige py-16 md:py-24">
        <div className="container-x">
          {VRIJ_WERK.length === 0 ? (
            <div data-testid="vrij-werk-empty" className="max-w-xl mx-auto text-center py-16">
              <p className="eyebrow mb-4 text-gold">Binnenkort</p>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight">
                Nieuw vrij werk <span className="italic text-gold">onderweg</span>
              </h2>
              <p className="mt-6 text-neutral-600 leading-relaxed">
                Momenteel zijn er geen werken beschikbaar. Kom binnenkort terug voor nieuwe beelden,
                of neem gerust contact op voor een print op maat.
              </p>
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {VRIJ_WERK.map((w, i) => (
              <motion.button
                key={w.id}
                data-testid={`${VW_TEST_IDS.card}-${w.id}`}
                onClick={() => openWork(w.id)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: i * 0.04 }}
                className="group text-left bg-white overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-500"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-neutral-100">
                  <img
                    src={w.src}
                    alt={w.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-110"
                  />
                  <div className="absolute top-3 right-3 bg-ink/85 text-white text-[10px] uppercase tracking-[0.25em] px-2.5 py-1">
                    Editie {w.editionOf}
                  </div>
                </div>
                <div className="p-6 flex items-baseline justify-between gap-4">
                  <div>
                    <h3 className="font-serif text-xl leading-tight">{w.title}</h3>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
                      {w.series} · {w.year}
                    </p>
                  </div>
                  <span className="font-serif text-lg text-gold whitespace-nowrap">{w.priceFrom}</span>
                </div>
              </motion.button>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* Ordering info */}
      <section className="bg-white py-20 md:py-24">
        <div className="container-narrow max-w-3xl text-center">
          <p className="eyebrow mb-3">Hoe werkt het</p>
          <h2 className="font-serif text-3xl md:text-4xl leading-tight">
            Van scherm naar <span className="italic text-gold">muur.</span>
          </h2>
          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
            {[
              { n: "01", t: "Kies een werk", d: "Blader door de galerij, klik op een beeld dat je raakt." },
              { n: "02", t: "Kies formaat & drager", d: "Fine art, canvas, dibond of plexi — elk werk is een beperkte oplage." },
              { n: "03", t: "Bestel & ontvang", d: "Bevestiging via WhatsApp of e-mail. Levering binnen 10 werkdagen." },
            ].map((s) => (
              <li key={s.n} className="border-t border-gold pt-5">
                <p className="font-script text-gold text-3xl leading-none">{s.n}</p>
                <p className="font-serif text-xl mt-2">{s.t}</p>
                <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{s.d}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CTASection />

      {/* Modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            data-testid={VW_TEST_IDS.modal}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4 md:p-8"
            onClick={() => setOpenId(null)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="relative bg-white max-w-5xl w-full max-h-[92vh] overflow-hidden grid grid-cols-1 md:grid-cols-5"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid={VW_TEST_IDS.modalClose}
                onClick={() => setOpenId(null)}
                className="absolute top-4 right-4 z-10 text-white md:text-ink hover:text-gold transition-colors bg-black/40 md:bg-transparent rounded-full p-1"
                aria-label="Sluiten"
              >
                <X size={22} />
              </button>

              {/* Image */}
              <div className="md:col-span-3 bg-neutral-100 relative">
                <img
                  src={active.src}
                  alt={active.title}
                  className="w-full h-full max-h-[50vh] md:max-h-[92vh] object-cover"
                  onContextMenu={(e) => e.preventDefault()}
                  draggable={false}
                />
              </div>

              {/* Info + purchase */}
              <div className="md:col-span-2 p-6 md:p-8 overflow-y-auto">
                <p className="eyebrow mb-2">{active.series} · {active.year}</p>
                <h3 className="font-serif text-3xl md:text-4xl leading-tight">{active.title}</h3>
                <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 mt-3">
                  Beperkte oplage — editie van {active.editionOf}
                </p>

                <p className="eyebrow mt-8 mb-4">Kies formaat</p>
                <div className="space-y-2">
                  {PRINT_SIZES.map((s) => {
                    const selected = sizeId === s.id;
                    return (
                      <button
                        key={s.id}
                        data-testid={`${VW_TEST_IDS.sizeOption}-${s.id}`}
                        onClick={() => setSizeId(s.id)}
                        className={`w-full flex items-center justify-between px-4 py-3 border text-left transition-colors ${
                          selected
                            ? "border-gold bg-beige"
                            : "border-neutral-200 hover:border-gold"
                        }`}
                      >
                        <span className="flex items-center gap-3 text-sm">
                          <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${selected ? "border-gold bg-gold" : "border-neutral-300"}`}>
                            {selected && <Check size={10} className="text-white" strokeWidth={3} />}
                          </span>
                          {s.label}
                        </span>
                        <span className={`font-serif text-lg ${selected ? "text-gold" : "text-neutral-700"}`}>
                          {s.price}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-8 pt-6 border-t border-neutral-200">
                  <p className="text-xs text-neutral-500 leading-relaxed mb-4">
                    Elk werk wordt genummerd en gesigneerd geleverd. Inclusief certificaat van
                    authenticiteit. Kader op aanvraag.
                  </p>
                  <a
                    data-testid={VW_TEST_IDS.orderBtn}
                    href={waLink(orderMsg || "")}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full btn-gold inline-flex items-center justify-center gap-2"
                  >
                    <ShoppingBag size={14} />
                    Bestel via WhatsApp
                  </a>
                  <a
                    href={mailtoLink(
                      `Bestelling: ${active.title} (${activeSize.label})`,
                      `Hallo Eric,\n\nGraag bestel ik het volgende vrije werk:\n\nTitel: ${active.title}\nJaar: ${active.year}\nFormaat: ${activeSize.label}\nPrijs: ${activeSize.price}\n\nBetaling via overschrijving op ${BRAND.iban} (Lenslopers Fotografie), met vermelding "${active.title} — ${activeSize.label}".\n\nMet vriendelijke groet,`
                    )}
                    className="mt-3 block text-center text-xs uppercase tracking-[0.3em] text-neutral-600 hover:text-gold transition-colors"
                  >
                    of bestel via e-mail
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default VrijWerk;
